import React, { useEffect, useState } from 'react';
import { LayoutTemplate, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Modal from '../ui/Modal';
import { CardContent, CardHeader, CardTitle } from '../ui/Card';
import ConfirmDialog from '../ui/ConfirmDialog';
import { criteriaToText } from './criteriaText';
import { segmentTemplatesService } from '../../services/segmentTemplatesService';

/**
 * Lists saved segment templates for an organization (plus legacy,
 * org-independent ones). `onPick(template)` receives the template
 * (`{ id, name, description, criteria }`). Org members can delete their own
 * org's templates; FastCube admins can also delete legacy (org-independent)
 * ones.
 */
const TemplatePicker = ({ isOpen, onClose, onPick, organizationId, isFastCubeAdmin = false }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    if (!isOpen || !organizationId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    segmentTemplatesService
      .list(organizationId)
      .then((rows) => {
        if (!cancelled) setTemplates(rows);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load templates');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, organizationId]);

  // The list is already scoped to organizationId (or legacy, org-independent
  // templates), so any org-owned row here is one the current user can write.
  const canManageTemplate = (t) => (t.organization_id == null ? isFastCubeAdmin : true);

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await segmentTemplatesService.remove(toDelete.id);
      setTemplates((prev) => prev.filter((t) => t.id !== toDelete.id));
      toast.success('Template deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete template');
    } finally {
      setToDelete(null);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-2xl max-h-[85vh] flex flex-col">
        <CardHeader className="border-b border-hairline pb-3 pr-10">
          <CardTitle className="flex items-center gap-2 text-lg text-[#18181B]">
            <LayoutTemplate className="h-5 w-5 text-zinc-400" />
            Start from a template
          </CardTitle>
          <p className="text-sm text-zinc-500">Pick a starting point, then adjust the conditions to fit.</p>
        </CardHeader>
        <CardContent className="min-h-0 flex-1 overflow-y-auto pt-4">
          {loading ? (
            <p className="flex items-center gap-2 text-sm text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
            </p>
          ) : error ? (
            <p className="text-sm text-red-600">{String(error)}</p>
          ) : templates.length === 0 ? (
            <p className="rounded-lg border border-dashed border-hairline px-4 py-8 text-center text-sm text-zinc-500">
              No templates yet. Open any segment and choose “Save as template” to create one.
            </p>
          ) : (
            <ul className="space-y-2">
              {templates.map((t) => (
                <li key={t.id} className="group flex items-start gap-3 rounded-xl border border-hairline bg-surface p-3 hover:bg-[#F8FAFC]">
                  <button type="button" onClick={() => onPick(t)} className="min-w-0 flex-1 text-left">
                    <p className="font-medium text-[#18181B]">{t.name}</p>
                    {t.description ? <p className="mt-0.5 text-sm text-zinc-600">{t.description}</p> : null}
                    <p className="mt-1 line-clamp-2 text-xs text-zinc-500">{criteriaToText(t.criteria)}</p>
                  </button>
                  {canManageTemplate(t) ? (
                    <button
                      type="button"
                      onClick={() => setToDelete(t)}
                      className="rounded p-1.5 text-zinc-300 opacity-0 transition-opacity hover:bg-pink-50 hover:text-[#F53D7A] group-hover:opacity-100 focus:opacity-100"
                      aria-label={`Delete template ${t.name}`}
                      title="Delete template"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Modal>
      <ConfirmDialog
        isOpen={toDelete != null}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
        title="Delete template"
        message={toDelete ? `Delete the template “${toDelete.name}”? Existing segments are not affected.` : ''}
        confirmText="Delete"
      />
    </>
  );
};

export default TemplatePicker;
